<script>
  // Both occurrence tables: the standalone /occurrences page and an event's
  // own Occurrences section. Props seed it with the page the request already
  // rendered, so the table is on screen before anything is fetched; every page
  // after that, and every search, comes back from GET /occurrences.json.
  //
  // Every string a row shows is the server's — the time zone, the formats and
  // the field values are all Rails' — so this island owns which page is on
  // screen and nothing about what a row says. Searching happens in the
  // database rather than over the loaded rows, which is the whole point: it
  // reaches occurrences that are not on this page.
  let {
    rows: initialRows = [],
    page: initialPage = 1,
    pages: initialPages = 1,
    count: initialCount = 0,
    query: initialQuery = "",
    fetchPath,
    eventId = null,
    columns = [],
    showsEventColumn = true,
    emptyMessage
  } = $props()

  // svelte-ignore state_referenced_locally -- islands remount per visit; props seed state once
  let rows = $state([...initialRows])
  // svelte-ignore state_referenced_locally
  let page = $state(initialPage)
  // svelte-ignore state_referenced_locally
  let pages = $state(initialPages)
  // svelte-ignore state_referenced_locally
  let count = $state(initialCount)
  // svelte-ignore state_referenced_locally
  let query = $state(initialQuery)
  let loading = $state(false)
  let input = $state()

  // Which gap's jump popup is open, as `${place}-${index}` — the pager is on
  // screen twice over one set of numbers, so the gaps need telling apart.
  let jumpOpen = $state(null)
  let jumpTo = $state("")

  // Only the newest request may write to the table. Typing a search while a
  // page fetch is still out means two answers are coming, and the slower one
  // is not allowed to put a stale page back on screen.
  let latestRequest = 0
  let debounce

  const SEARCH_DELAY = 250
  const ELLIPSIS = "…"

  // Read at mount: Turbo replaces the body and the islands remount with it, so
  // this is always the token of the page currently on screen.
  const csrfToken = document.querySelector("meta[name='csrf-token']")?.content ?? ""

  const searching = $derived(query.trim() !== "")
  const showsTable = $derived(count > 0 || searching)
  const numbers = $derived(pageNumbers(page, pages))

  // First page, last page and the pages either side of this one, with a gap
  // marked wherever that skips something. Enough to get anywhere in two clicks
  // without a strip of numbers as long as the table.
  function pageNumbers(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1)

    const near = [current - 1, current, current + 1].filter((one) => one > 1 && one < total)
    const listed = []

    for (const number of [1, ...near, total]) {
      const previous = listed[listed.length - 1]
      if (previous !== undefined && number - previous > 1) listed.push(ELLIPSIS)
      listed.push(number)
    }

    return listed
  }

  function toggleJump(key) {
    if (jumpOpen === key) {
      jumpOpen = null
      return
    }

    jumpTo = String(page)
    jumpOpen = key
  }

  function jump() {
    const wanted = Number.parseInt(jumpTo, 10)
    jumpOpen = null
    if (Number.isNaN(wanted)) return

    load(Math.min(Math.max(wanted, 1), pages))
  }

  function jumpKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      jump()
    } else if (event.key === "Escape") {
      event.stopPropagation()
      jumpOpen = null
    }
  }

  // Anywhere outside the popup taking focus closes it — including the page
  // behind it, which focuses the body and reports no related target.
  function closeJumpOnLeave(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) jumpOpen = null
  }

  function focusOnMount(node) {
    node.focus()
    node.select()
  }

  function reportFailure(message) {
    document.dispatchEvent(new CustomEvent("toast", { detail: { type: "alert", message } }))
  }

  function pageUrl(wanted) {
    const url = new URL(fetchPath, window.location.origin)
    url.searchParams.set("page", wanted)
    if (searching) url.searchParams.set("q", query.trim())
    if (eventId !== null) url.searchParams.set("event_id", eventId)
    return url
  }

  // The table on screen stays put until the answer arrives, so nothing blanks
  // and nothing jumps while a page is in flight.
  async function load(wanted) {
    const sequence = ++latestRequest
    const url = pageUrl(wanted)
    jumpOpen = null
    loading = true

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken }
      })

      if (!response.ok) throw new Error(`GET ${url} answered ${response.status}`)

      const answer = await response.json()
      if (sequence !== latestRequest) return

      rows = answer.rows
      page = answer.page
      pages = answer.pages
      count = answer.count
    } catch {
      if (sequence === latestRequest) {
        reportFailure("Could not load those occurrences. Reload the page and try again.")
      }
    } finally {
      if (sequence === latestRequest) loading = false
    }
  }

  // A search is a new first page: which page someone was on says nothing about
  // where its results are.
  function search() {
    clearTimeout(debounce)
    debounce = setTimeout(() => load(1), SEARCH_DELAY)
  }

  function clear() {
    query = ""
    input.focus()
    search()
  }

  // The page is fetched again rather than patched, so the count and the page
  // numbers stay honest. Deleting the only row left on a page takes the page
  // with it, and there is nothing to show at that number any more.
  async function destroy(row) {
    if (!confirm("Are you sure you want to delete this occurrence?")) return

    try {
      const response = await fetch(row.deletePath, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken }
      })

      if (!response.ok) throw new Error(`DELETE ${row.deletePath} answered ${response.status}`)

      await load(rows.length === 1 && page > 1 ? page - 1 : page)
    } catch {
      reportFailure("Could not delete that occurrence. Reload the page and try again.")
    }
  }

  // "/" is only a shortcut while the visitor is not already typing somewhere.
  function editingText() {
    const tag = document.activeElement?.tagName
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
  }

  $effect(() => {
    function handleKeydown(event) {
      if (event.key === "/" && !editingText()) {
        event.preventDefault()
        input?.focus()
      } else if (event.key === "Escape" && document.activeElement === input) {
        clear()
        input.blur()
      }
    }

    document.addEventListener("keydown", handleKeydown)
    return () => document.removeEventListener("keydown", handleKeydown)
  })

  $effect(() => () => clearTimeout(debounce))
</script>

{#snippet step(label, target, unreachable, glyph)}
  <li>
    <button
      type="button"
      class="pagination-link"
      aria-label={label}
      disabled={unreachable}
      onclick={() => load(target)}>{glyph}</button
    >
  </li>
{/snippet}

<!-- Rendered above and below the table over the one set of page state, so a
     long page needs no scroll back to reach the next one. Every control sits
     in the single `pagination-list` rather than in Bulma's previous/next
     slots, which reorder themselves around it: this is the one place the
     order first-previous-numbers-next-last is written down. -->
{#snippet pager(place)}
  <nav
    class="pagination is-small is-centered"
    aria-label={place === "top" ? "Pagination above the table" : "Pagination below the table"}
  >
    <ul class="pagination-list">
      {@render step("First page", 1, page === 1, "«")}
      {@render step("Previous page", page - 1, page === 1, "‹")}

      {#each numbers as number, index (index)}
        <li>
          {#if number === ELLIPSIS}
            <div class="jump" onfocusout={closeJumpOnLeave}>
              <button
                type="button"
                class="pagination-ellipsis"
                aria-label="Jump to a page"
                aria-expanded={jumpOpen === `${place}-${index}`}
                onclick={() => toggleJump(`${place}-${index}`)}>&hellip;</button
              >

              {#if jumpOpen === `${place}-${index}`}
                <div class="jump-popup">
                  <input
                    type="number"
                    class="input is-small jump-input"
                    min="1"
                    max={pages}
                    aria-label="Page number"
                    bind:value={jumpTo}
                    onkeydown={jumpKeydown}
                    use:focusOnMount
                  />
                  <!-- Taking focus off the input would close the popup before
                       the click landed, so the button never takes it. -->
                  <button
                    type="button"
                    class="button is-small is-primary"
                    onmousedown={(event) => event.preventDefault()}
                    onclick={jump}>Go</button
                  >
                </div>
              {/if}
            </div>
          {:else}
            <button
              type="button"
              class="pagination-link"
              class:is-current={number === page}
              aria-label={`Go to page ${number}`}
              aria-current={number === page ? "page" : undefined}
              onclick={() => load(number)}>{number}</button
            >
          {/if}
        </li>
      {/each}

      {@render step("Next page", page + 1, page === pages, "›")}
      {@render step("Last page", pages, page === pages, "»")}
    </ul>
  </nav>
{/snippet}

{#if showsTable}
  <div class="search-container">
    <input
      type="text"
      id="occurrence-search"
      name="q"
      class="input search-input"
      placeholder="Search occurrences… (press / to focus)"
      autocomplete="off"
      autocapitalize="off"
      autocorrect="off"
      spellcheck="false"
      bind:value={query}
      bind:this={input}
      oninput={search}
    />
    <button
      type="button"
      class="delete search-clear"
      aria-label="Clear search"
      hidden={!query}
      onclick={clear}
    ></button>
  </div>

  {#if pages > 1}
    {@render pager("top")}
  {/if}

  <div class="table-container" class:is-loading={loading} aria-busy={loading}>
    <table class="table is-fullwidth is-hoverable">
      <thead>
        <tr>
          {#each columns as header, index (index)}
            <th>{header}</th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each rows as row (row.id)}
          <tr>
            {#if showsEventColumn}
              <td><a href={row.showPath}>{row.eventName}</a></td>
              <td>{row.occurredAt}</td>
            {:else}
              <td><a href={row.showPath}>{row.occurredAt}</a></td>
              {#each row.fieldValues as value, index (index)}
                <td>
                  {#if value.href}
                    <a href={value.href}>{value.text}</a>
                  {:else}
                    {value.text}
                  {/if}
                </td>
              {/each}
            {/if}
            <td>{row.notes}</td>
            <td>
              <div class="buttons are-small">
                <a class="button is-small is-primary" href={row.editPath}>Edit</a>
                <button type="button" class="button is-small is-danger" onclick={() => destroy(row)}>
                  Delete
                </button>
              </div>
            </td>
          </tr>
        {/each}

        {#if rows.length === 0}
          <tr>
            <td colspan={columns.length} class="has-text-centered">No matching records found.</td>
          </tr>
        {/if}
      </tbody>
    </table>
  </div>

  <div class="level is-mobile mt-3">
    <div class="level-left">
      <p class="level-item is-size-7 has-text-grey" data-occurrence-count>
        {count}
        {count === 1 ? "occurrence" : "occurrences"}
      </p>
    </div>
  </div>

  {#if pages > 1}
    {@render pager("bottom")}
  {/if}
{:else}
  <p>
    {emptyMessage.text}
    {#if emptyMessage.link}
      <a href={emptyMessage.link.href}>{emptyMessage.link.label}</a>
      {emptyMessage.suffix}
    {/if}
  </p>
{/if}

<style>
  .search-container {
    position: relative;
    margin-bottom: 1rem;
  }

  /* Room for the clear button overlaid on the input's right edge. */
  input {
    padding-right: 2.5rem;
  }

  .search-clear {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
  }

  /* A page in flight leaves the rows it is replacing on screen — dimmed, so
     nothing moves and nothing looks final. */
  .table-container.is-loading {
    opacity: 0.6;
  }

  .jump {
    position: relative;
  }

  /* Bulma's ellipsis is a label rather than a control: it refuses the pointer
     and wears the user agent's button chrome once it becomes one. */
  .pagination-ellipsis {
    background: none;
    cursor: pointer;
    pointer-events: auto;
  }

  .jump-popup {
    position: absolute;
    top: 100%;
    left: 50%;
    z-index: 20;
    display: flex;
    gap: 0.25rem;
    transform: translateX(-50%);
    padding: 0.5rem;
    border: 1px solid var(--bulma-border);
    border-radius: var(--bulma-radius);
    background-color: var(--bulma-scheme-main);
    box-shadow: var(--bulma-shadow);
  }

  .jump-input {
    width: 5rem;
  }
</style>
